import { useEffect, useRef } from "react";

export type WakeLockSentinelLike = {
  release: () => Promise<void>;
  addEventListener: (type: "release", listener: () => void) => void;
};

export type WakeLockLike = {
  request: (type: "screen") => Promise<WakeLockSentinelLike>;
};

function getWakeLock(): WakeLockLike | undefined {
  if (typeof navigator === "undefined") return undefined;
  return (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock;
}

export async function requestScreenWakeLock(
  wakeLock: WakeLockLike | undefined,
  isVisible: boolean,
): Promise<WakeLockSentinelLike | null> {
  if (!wakeLock || !isVisible) return null;
  try {
    return await wakeLock.request("screen");
  } catch {
    return null;
  }
}

export function useScreenWakeLock(active: boolean): void {
  const sentinelRef = useRef<WakeLockSentinelLike | null>(null);

  useEffect(() => {
    if (!active) return;
    let disposed = false;
    let acquiring = false;

    const acquire = async () => {
      if (disposed || acquiring || sentinelRef.current) return;
      acquiring = true;
      try {
        const isVisible = typeof document === "undefined" || document.visibilityState === "visible";
        const sentinel = await requestScreenWakeLock(getWakeLock(), isVisible);
        if (!sentinel) return;
        if (disposed) {
          void sentinel.release().catch(() => undefined);
          return;
        }
        sentinelRef.current = sentinel;
        sentinel.addEventListener("release", () => {
          if (sentinelRef.current === sentinel) sentinelRef.current = null;
        });
      } finally {
        acquiring = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void acquire();
    };

    void acquire();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      const sentinel = sentinelRef.current;
      sentinelRef.current = null;
      if (sentinel) void sentinel.release().catch(() => undefined);
    };
  }, [active]);
}
